import { h, defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { loader, loadingButton } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, updateOAuth, updateSaml, getErrorLogs } from '@/services/api/resources/sso-configuration/actions';
import { versioned } from '@/services/api';
import { useSsoConfigurationForm } from './useSsoConfigurationForm';
import { useSsoConfigurationRecord } from './useSsoConfigurationRecord';
import { AdministrationTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { user } from '@/ui/auth/auth';
import { pushRoute, toastAndRedirect } from '@/ui/helpers';
import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
import { InformationModal } from '@/ui/components/modals/InformationModal';
import { ErrorLogsModal } from '@/ui/components/sso/ErrorLogsModal';
import { ScimErrorLog } from '@/services/api/resources/sso-configuration';
import { v4 as uuidv4 } from 'uuid';

export const SsoConfigurationForm = defineComponent({
  name: 'SsoConfigurationForm',
  props: [],
  setup() {
    let recordVersion = '';
    const organizationId = user.value.organization.id;

    // create empty record
    const record = useSsoConfigurationRecord();
    const errorLogs = ref(<ScimErrorLog[]>[]);

    // get record
    const getRecord = useTask(
      () => get(organizationId),
      ({ data, version }) => {
        if (data.data.configuration && 'certificate' in data.data.configuration) {
          data.data.configuration.certificate = typeof data.data.configuration.certificate === 'string'
            ? new File([atob(<string>data.data.configuration.certificate)], 'certificate.crt')
            : null;
        }
        record.value = data.data;
        recordVersion = version;
      },
    );

    // get record
    const getErrorLogsTask = useTask(
      () => getErrorLogs(user.value.organization.id),
      ({ data }) => {
        errorLogs.value = data;
      },
    );

    // send record
    const sendRecord = useTask(
      () => {
        if (record.value.identityProviderType === 'oauth2') {
          return updateOAuth(organizationId, versioned(record.value, recordVersion));
        }

        return updateSaml(organizationId, versioned(record.value, recordVersion));
      },
      () => {
        isConfirmationScimModalOpen.value = isScimConfirmationModalRequired.value;
        isScimConfirmationModalRequired.value = false;

        return toastAndRedirect(t('sso.edited'), 'administration', { [queryTabName]: AdministrationTabNames.SSO });
      },
      uuidv4(),
    );

    // form
    const isScimConfirmationModalRequired = ref(false);
    const isConfirmationModalOpen = ref(false);
    const isConfirmationScimModalOpen = ref(false);
    const isErrorLogsModalOpen = ref(false);
    const {
      formFields,
      handleSubmit,
    } = useSsoConfigurationForm(record, sendRecord, isScimConfirmationModalRequired, isErrorLogsModalOpen);
    const redirectToRolesPage = () => pushRoute({ name: 'administration', query: { tab: 'roles' } });

    return function render() {
      return h('div', [
        h(ConfirmationModal, {
          props: {
            active: isConfirmationModalOpen.value,
            title: t('sso.confirm-dialog-title'),
            width: '540px',
            confirmText: t('sso.send'),
            declineText: t('common.cancel'),
          },
          on: {
            declined: () => {
              isConfirmationModalOpen.value = false;
            },
            accepted: () => {
              isConfirmationModalOpen.value = false;
              handleSubmit();
            },
          },
        }, [t('sso.confirm-dialog')]),
        h(InformationModal, {
          props: {
            active: isConfirmationScimModalOpen.value,
            width: '500px',
            confirmText: t('sso.scim.role-modal.button'),
          },
          scopedSlots: {
            default: () => [
              h('h1', { class: 'sso-scim-role-information-modal__title' }, [t('sso.scim.role-modal.title')]),
              h('p', { class: 'sso-scim-role-information-modal__text' }, [t('sso.scim.role-modal.description')]),
            ],
          },
          on: {
            close: () => {
              isConfirmationScimModalOpen.value = false;
            },
            accepted: () => {
              redirectToRolesPage();
              isConfirmationScimModalOpen.value = false;
            },
          },
        }, [t('sso.confirm-dialog')]),
        isErrorLogsModalOpen.value && [loader('form', getErrorLogsTask, [
          h(ErrorLogsModal, {
            props: {
              active: isErrorLogsModalOpen.value,
              width: '500px',
              closeText: t('common.close'),
              logs: errorLogs.value,
            },
            on: {
              close: () => {
                isErrorLogsModalOpen.value = false;
              },
              accepted: () => {
                isErrorLogsModalOpen.value = false;
              },
            },
          }, [t('sso.confirm-dialog')]),
        ])],
        h('div', [loader('form', getRecord, [
          ...formFields(),
          h('div', { class: 'sso-configuration-actions' }, [
            loadingButton(
              t('sso.update'),
              () => {
                isConfirmationModalOpen.value = true;
              },
              sendRecord.state.value.pending,
            ),
            // @todo uncomment when the link is ready
            // HelpCenterLink(),
          ]),
        ])]),
      ]);
    };
  },
});
